import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { BungalowDataProps } from 'components/shared/Bungalow'

export interface PeopleState extends BungalowDataProps {
    total: number
}
export interface GuestState {
    name: string
    surname: string
}
interface BookingPersonState extends GuestState {
    email: string
    phone: string
}

export interface BungalowInfo {
    adults: GuestState[]
    kids: GuestState[]
    infants: GuestState[]
    room?: SpecificRoom | null
}
export interface SpecificRoom {
    bungalowIndex: number
    roomId: string
    rateId: string
    occupancyId: string
    productId: string
    rateName?: string
}
interface AccommodationState {
    dates: string[]
    guestsToAssign: PeopleState
    isAssignGuestsVisible: boolean
    areBungalowsVisible: boolean
    bungalows: BungalowInfo[]
    bookingPerson: BookingPersonState
    specialRequests?: string
    isAccommodationInfoModalVisible: boolean
    // indicates if actions with accommodation should consider connected promotions
    // - actually: city break
    isAccommodationPromotionApplicable: boolean
}

const bookingPerson: BookingPersonState = {
    name: '',
    surname: '',
    email: '',
    phone: '',
}
export const emptyBungalow: BungalowInfo = {
    adults: [],
    kids: [],
    infants: [],
    room: null,
}

const initialState: AccommodationState = {
    dates: [],
    guestsToAssign: {
        total: 0,
        adults: 0,
        kids: 0,
        infants: 0,
    },
    isAssignGuestsVisible: false,
    areBungalowsVisible: false,
    bungalows: [emptyBungalow],
    bookingPerson,
    specialRequests: '',
    isAccommodationInfoModalVisible: false,
    isAccommodationPromotionApplicable: false,
}

export type AccommodationEditPoint =
    | 'guests'
    | 'bungalows'
    | 'offers'
    | undefined

const accommodationSlice = createSlice({
    name: 'Accommodation',
    initialState,
    reducers: {
        setDates(state, action: PayloadAction<string[]>) {
            state.dates = action.payload
        },
        updateGuestsToAssign(state, action: PayloadAction<PeopleState>) {
            state.guestsToAssign = action.payload
        },
        removeGuestsToAssign(state) {
            state.guestsToAssign = initialState.guestsToAssign
            state.isAssignGuestsVisible = initialState.isAssignGuestsVisible
        },
        setBungalowsVisible(state, action: PayloadAction<boolean>) {
            state.areBungalowsVisible = action.payload
        },
        setAssignGuestsVisible(state, action: PayloadAction<boolean>) {
            state.isAssignGuestsVisible = action.payload
        },
        setBungalows(state, action: PayloadAction<BungalowInfo[]>) {
            state.bungalows = action.payload
        },
        removeAllBungalows(state) {
            state.bungalows = initialState.bungalows
        },
        setBookingPersonDetails(
            state,
            action: PayloadAction<{
                field: 'name' | 'surname' | 'email' | 'phone'
                value: string
            }>
        ) {
            const { field, value } = action.payload
            state.bookingPerson[field] = value
        },
        removeBookingPersonDetails(state) {
            state.bookingPerson = initialState.bookingPerson
        },
        setGuestDetails(
            state,
            action: PayloadAction<{
                field: 'name' | 'surname'
                type: 'adults' | 'kids' | 'infants'
                bungalowIndex: number
                index: number
                value: string
            }>
        ) {
            const { field, bungalowIndex, type, index, value } = action.payload
            state.bungalows[bungalowIndex][type][index][field] = value
        },
        updateChosenRooms(state, action: PayloadAction<SpecificRoom>) {
            const { bungalowIndex } = action.payload

            if (state.bungalows[bungalowIndex]) {
                state.bungalows[bungalowIndex].room = action.payload
            } else {
                state.bungalows[bungalowIndex] = {
                    ...emptyBungalow,
                    ...action.payload,
                }
            }
        },
        removeRoom(state, action: PayloadAction<number>) {
            const bungalowIndex = action.payload
            state.bungalows[bungalowIndex].room = null
        },
        removeAllRooms(state) {
            state.bungalows = state.bungalows.map((bungalow) => ({
                ...bungalow,
                room: null,
            }))
        },
        setSpecialRequests(state, action: PayloadAction<string>) {
            state.specialRequests =
                action.payload !== '' ? action.payload : undefined
        },
        removeAccommodationFromStore(
            state,
            action: PayloadAction<AccommodationEditPoint>
        ) {
            const editPoint = action.payload || ''

            return {
                ...initialState,
                // do not reset specific settings based on current 'edit point' in accommodation configurator
                ...(['guests', 'bungalows', 'offers'].includes(editPoint) && {
                    dates: [...state.dates],
                    guestsToAssign: { ...state.guestsToAssign },
                }),
                ...(['bungalows', 'offers'].includes(editPoint) && {
                    isAssignGuestsVisible: true,
                    bungalows: [...state.bungalows],
                }),
                ...(['offers'].includes(editPoint) && {
                    areBungalowsVisible: true,
                    bungalows: [...state.bungalows],
                    bookingPerson: { ...state.bookingPerson },
                    specialRequests: state.specialRequests,
                }),
            }
        },
        setAccommodationInfoModalVisible(
            state,
            action: PayloadAction<boolean>
        ) {
            state.isAccommodationInfoModalVisible = action.payload
        },
        setAccommodationPromotionApplicable(
            state,
            action: PayloadAction<boolean>
        ) {
            state.isAccommodationPromotionApplicable = action.payload
        },
    },
})

export const {
    setDates,
    updateGuestsToAssign,
    setBungalowsVisible,
    setAssignGuestsVisible,
    setBungalows,
    setBookingPersonDetails,
    setGuestDetails,
    updateChosenRooms,
    removeAccommodationFromStore,
    removeAllBungalows,
    removeBookingPersonDetails,
    removeGuestsToAssign,
    removeRoom,
    removeAllRooms,
    setSpecialRequests,
    setAccommodationInfoModalVisible,
    setAccommodationPromotionApplicable,
} = accommodationSlice.actions
export default accommodationSlice.reducer
